import React, { useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faCircleCheck, faLock } from "@fortawesome/free-solid-svg-icons";
import DynamicLayout from "../../../components/DynamicLayout";
import ProgressBar from "../../components/ProgressBar";
import { AcademyContext } from "../../../context/academyContext";
import saturnwallpaper from "../../../img/saturnwallpaper.jpg";
import { useNavigate } from "react-router-dom";

const RunesAcademy = () => {
  const { progress } = useContext(AcademyContext);
  const [activeChapter, setActiveChapter] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [activePart, setActivePart] = useState(null);
  const [selectedMode, setSelectedMode] = useState("beginner"); 
  const navigate = useNavigate();

  const totalLessons = 30; // Updated total number of lessons in the runes course

  const toggleChapter = (chapter) => {
    setActiveChapter(activeChapter === chapter ? null : chapter);
  };

  const toggleLesson = (lesson) => {
    setActiveLesson(activeLesson === lesson ? null : lesson);
  };

  const togglePart = (part) => {
    setActivePart(activePart === part ? null : part);
  };

  const renderIcon = (chapter, isAvailable) => {
    return (
      <FontAwesomeIcon
        icon={activeChapter === chapter ? faChevronUp : faChevronDown}
        className={isAvailable ? "has-text-warning" : "has-text-grey"}
      />
    );
  };

  const renderLessonIcon = (lesson, isAvailable) => {
    return (
      <FontAwesomeIcon
        icon={activeLesson === lesson ? faChevronUp : faChevronDown}
        className={isAvailable ? "has-text-warning" : "has-text-grey"}
      />
    );
  };

  const renderPartIcon = (part, isAvailable) => {
    return (
      <FontAwesomeIcon
        icon={activePart === part ? faChevronUp : faChevronDown}
        className={isAvailable ? "has-text-warning" : "has-text-grey"}
      />
    );
  };

  const renderCheckMark = (lessonNumber) => {
    return progress.runes?.completedLessons >= lessonNumber ? (
      <FontAwesomeIcon icon={faCircleCheck} className="has-text-warning ml-2" />
    ) : (
      <FontAwesomeIcon icon={faLock} className="has-text-grey ml-2" />
    );
  };

  const handleResumeClick = () => {
    let lessonNumber = progress.runes?.completedLessons + 1;
    const path = selectedMode === "beginner" ? "beginner" : "intermediate";
    navigate(`/academy/runes/${path}/lesson?lesson=${lessonNumber}`);
  };

  const handleModeChange = (mode) => {
    setSelectedMode(mode);
  };

  const renderLesson = (lessonNumber, lessonTitle) => {
    const isCompleted = progress.runes?.completedLessons >= lessonNumber;
    return (
      <li
        className={`${isCompleted ? "is-hoverable has-text-warning" : "has-text-grey"}`}
        style={{ cursor: isCompleted ? 'pointer' : 'default' }}
        onClick={() => isCompleted && navigate(`/academy/runes/${selectedMode}/lesson?lesson=${lessonNumber}`)}
      >
        <span className="lesson-title">
          {lessonTitle} {renderCheckMark(lessonNumber)}
        </span>
      </li>
    );
  };

  const renderCollapsibleLesson = (lessonNumber, lessonTitle, parts) => {
    const isCompleted = progress.runes?.completedLessons >= lessonNumber;
    return (
      <li
        className={`${isCompleted ? "has-text-warning" : "has-text-grey"} `}
        onClick={() => toggleLesson(lessonTitle)}
        style={{ cursor: 'pointer' }}
      >
        {lessonTitle} {renderLessonIcon(lessonTitle, isCompleted)}
        <ul className={activeLesson === lessonTitle ? "" : "is-hidden"}>
          {parts}
        </ul>
      </li>
    );
  };

  const renderPart = (partNumber, partTitle, lessons) => {
    const isCompleted = progress.runes?.completedLessons >= partNumber;
    return (
      <li
        className={`${isCompleted ? "has-text-warning" : "has-text-grey"}`}
        onClick={() => togglePart(partTitle)}
        style={{ cursor: 'pointer' }}
      >
        {partTitle} {renderPartIcon(partTitle, isCompleted)}
        <ul className={activePart === partTitle ? "" : "is-hidden"}>
          {lessons}
        </ul>
      </li>
    );
  };

  const hasStartedCourse = progress.runes?.completedLessons > 0;
  const isHardModeUnlocked = true; // Temporarily enable Intermediate mode for development

  return (
    <DynamicLayout>
      <div
        className="full-screen-image-container heightauto margin-top-0 has-text-warning"
        style={{
          backgroundImage: `url(${saturnwallpaper})`,
        }}
      >
        <section className="section hasbackgroundgrey heightauto has-text-centered has-text-warning">
          <h2 className="title has-text-centered has-text-warning">
            {hasStartedCourse ? "Welcome back to the Runes Course." : "Welcome to the Runes Course."}
          </h2>
        
          <ProgressBar completedLessons={progress.runes?.completedLessons} totalLessons={totalLessons} />
          
          <ul className="has-text-warning">
            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.runes?.completedLessons >= 1 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter1")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 1: Introduction to Runes {renderIcon("chapter1", progress.runes?.completedLessons >= 1)}
              </h2>
              <ul className={activeChapter === "chapter1" ? "" : "is-hidden"}>
                {renderLesson(1, "Lesson 1: Historical Origins and Development")}
                {renderLesson(2, "Lesson 2: Structure of the Runic Alphabet")}
              </ul>
            </li>
            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.runes?.completedLessons >= 3 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter2")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 2: Learning the Runes {renderIcon("chapter2", progress.runes?.completedLessons >= 3)}
              </h2>
              <ul className={activeChapter === "chapter2" ? "" : "is-hidden"}>
                {renderLesson(3, "Lesson 1: Fehu")}
                {renderLesson(4, "Lesson 2: Uruz")}
                {renderLesson(5, "Lesson 3: Thurisaz")}
                {renderLesson(6, "Lesson 4: Ansuz")}
                {renderLesson(7, "Lesson 5: Raidho")}
                {renderLesson(8, "Lesson 6: Kenaz")}
                {renderLesson(9, "Lesson 7: Gebo")}
                {renderLesson(10, "Lesson 8: Wunjo")}
                {renderLesson(11, "Lesson 9: Hagalaz")}
                {renderLesson(12, "Lesson 10: Nauthiz")}
                {renderLesson(13, "Lesson 11: Isa")}
                {renderLesson(14, "Lesson 12: Jera")}
                {renderLesson(15, "Lesson 13: Eihwaz")}
                {renderLesson(16, "Lesson 14: Perthro")}
                {renderLesson(17, "Lesson 15: Algiz")}
                {renderLesson(18, "Lesson 16: Sowilo")}
                {renderLesson(19, "Lesson 17: Tiwaz")}
                {renderLesson(20, "Lesson 18: Berkano")}
                {renderLesson(21, "Lesson 19: Ehwaz")}
                {renderLesson(22, "Lesson 20: Mannaz")}
                {renderLesson(23, "Lesson 21: Laguz")}
                {renderLesson(24, "Lesson 22: Ingwaz")}
                {renderLesson(25, "Lesson 23: Dagaz")}
                {renderLesson(26, "Lesson 24: Othala")}
              </ul>
            </li>
            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.runes?.completedLessons >= 27 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter3")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 3: Runic Divination {renderIcon("chapter3", progress.runes?.completedLessons >= 27)}
              </h2>
              <ul className={activeChapter === "chapter3" ? "" : "is-hidden"}>
                {renderLesson(27, "Lesson 1: Ethics and Responsibilities")}
                {renderLesson(28, "Lesson 2: Preparing for Divination")}
                {renderLesson(29, "Lesson 3: Basic Divination Techniques")}
                {renderLesson(30, "Lesson 4: Advanced Divination Spreads")}
              </ul>
            </li>
          </ul>

          <button className="button is-warning mt-6" onClick={handleResumeClick}>
            {hasStartedCourse ? "Resume" : "Start"}
          </button>

          <div className="mt-6 buttons has-addons is-centered">
            <button
              className={`button is-small ${selectedMode === "beginner" ? "is-warning" : ""}`}
              onClick={() => handleModeChange("beginner")}
            >
              Beginner
            </button>
            <button
              className={`button is-small ${selectedMode === "intermediate" ? "is-warning" : ""} ${isHardModeUnlocked ? '' : 'is-locked'}`}
              onClick={() => handleModeChange("intermediate")}
              disabled={!isHardModeUnlocked}
              title={isHardModeUnlocked ? '' : 'Complete all lessons to unlock Intermediate mode'}
            >
              {!isHardModeUnlocked && <FontAwesomeIcon icon={faLock} className="lock-icon" />}
              Intermediate
            </button>
          </div>

        </section>
      </div>
    </DynamicLayout>
  );
};

export default RunesAcademy;
